import { useFileAttachments } from '../hooks/useFileAttachments';
import Trash2Icon from './icons/Trash2Icon';
import FileIcon from './icons/FileIcon';
import DownloadIcon from './icons/DownloadIcon';
import EyeIcon from './icons/EyeIcon';
import { SecureImage } from './SecureImage';
import { formatDateOnly } from '../utils/dateFormat';

interface CardAttachmentsProps {
  cardId?: number;
}

export function CardAttachments({ cardId }: CardAttachmentsProps) {
  const { attachments, uploadFile, deleteAttachment } = useFileAttachments(cardId);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadFile(file);
    } catch {
      // Error already handled in hook with toast
    }

    // Reset input
    e.target.value = '';
  };

  const handleDelete = async (fileUuid: string, fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    await deleteAttachment(fileUuid);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <span className="w-5 h-5">
            <FileIcon />
          </span>
          Attachments
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">{attachments.length} files</span>
          <label className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors cursor-pointer flex items-center gap-2 text-sm">
            Add Attachment
            <input
              type="file"
              className="hidden"
              onChange={handleFileUpload}
              accept="image/*,.pdf"
            />
          </label>
        </div>
      </div>

      {attachments.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p>No attachments yet</p>
          <p className="text-sm mt-2">Upload images or PDFs to this card</p>
        </div>
      ) : (
        <div className="space-y-3">
          {attachments.map((attachment) => (
            <div
              key={attachment.uuid}
              className="bg-gray-700 rounded-lg p-4 hover:bg-gray-650 transition-colors"
            >
              <div className="flex items-start gap-4">
                {/* Thumbnail or file icon */}
                <div className="flex-shrink-0">
                  {isImage(attachment.mime_type) && attachment.thumbnail_url ? (
                    <SecureImage
                      url={attachment.thumbnail_url}
                      alt={attachment.original_name}
                      className="w-16 h-16 object-cover rounded"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-600 rounded flex items-center justify-center">
                      <span className="w-8 h-8 text-gray-400">
                        <FileIcon />
                      </span>
                    </div>
                  )}
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{attachment.original_name}</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {formatFileSize(attachment.file_size)} •{' '}
                        {formatDateOnly(attachment.created_at)}
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isImage(attachment.mime_type) && attachment.view_url && (
                        <a
                          href={attachment.view_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 transition-colors p-2"
                          title="View"
                        >
                          <span className="w-4 h-4">
                            <EyeIcon />
                          </span>
                        </a>
                      )}
                      {attachment.download_url && (
                        <a
                          href={attachment.download_url}
                          className="text-blue-400 hover:text-blue-300 transition-colors p-2"
                          title="Download"
                        >
                          <span className="w-4 h-4">
                            <DownloadIcon />
                          </span>
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(attachment.uuid, attachment.original_name)}
                        className="text-red-400 hover:text-red-300 transition-colors p-2"
                        title="Delete"
                      >
                        <span className="w-4 h-4">
                          <Trash2Icon />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
